import React from "react";
import AccountContext, { DEFAULT_ACCOUNT_CONTEXT } from "./AccountContext";
import { IAccountContext } from "./types";

/**
 * Returns the {@link IAccountContext} value from the nearest
 * {@link AccountContextProvider} in the tree.
 *
 * @throws {Error} if called outside an `AccountContextProvider`.
 *
 * @example
 * ```tsx
 * function AccountSwitcher() {
 *   const { contextId, setContextId, availableContexts } = useAccountContext();
 *   if (availableContexts.length <= 1) return null;
 *   return (
 *     <Select value={contextId ?? rootAccountId} onChange={e => {
 *       const id = e.target.value as string;
 *       setContextId(id === rootAccountId ? null : id);
 *     }}>
 *       {availableContexts.map(ctx => (
 *         <MenuItem key={ctx.id} value={ctx.id}>{ctx.displayName}</MenuItem>
 *       ))}
 *     </Select>
 *   );
 * }
 * ```
 */
export function useAccountContext(): IAccountContext {
  const ctx = React.useContext(AccountContext);

  if (ctx === DEFAULT_ACCOUNT_CONTEXT) {
    throw new Error("useAccountContext must be called within an AccountContextProvider");
  }

  return ctx;
}

export default useAccountContext;
